import { Trans } from "@lingui/macro"
import Link, { LinkProps } from "next/link"
import { ReactNode } from "react"
import ArrowLeftIcon from "remixicon-react/ArrowLeftSLineIcon"
import ArrowRightIcon from "remixicon-react/ArrowRightSLineIcon"
import MoreIcon from "remixicon-react/MoreFillIcon"

import { UsePaginationProps, usePagination } from "@/domains/pagination/lib"
import { CSS, styled } from "@/stitches"
import { Button, IconButton } from "@/ui/button"

import { Box } from "./box"

const Container = styled("nav", {
  display: "inline-flex",
  alignItems: "center",
  gap: "$2",

  ol: {
    display: "flex",
    alignItems: "center",
    gap: "$1",
    overflowX: "auto",

    "@md": {
      gap: "$2",
    },
  },

  li: {
    flexShrink: 0,
  },
})

const StyledMoreIcon = styled(MoreIcon, {
  display: "block",
  color: "$gray11",
  width: "1.25rem",
  mx: "$2",
})

function StepButton({
  href,
  iconBefore,
  iconAfter,
  children,
}: {
  children: ReactNode
  iconBefore?: ReactNode
  iconAfter?: ReactNode
  href: LinkProps["href"]
}) {
  return (
    <>
      <Link href={href} passHref>
        <IconButton
          as="a"
          size="sm2"
          css={{
            "@md": { display: "none" },
          }}
        >
          {iconBefore || iconAfter}
        </IconButton>
      </Link>
      <Link href={href} passHref>
        <Button
          as="a"
          variant="ghost"
          color="secondary"
          density="dense"
          iconBefore={iconBefore}
          iconAfter={iconAfter}
          css={{
            display: "none",
            "@md": { display: "flex" },
          }}
        >
          {children}
        </Button>
      </Link>
    </>
  )
}

export function PageNavContainer({
  hrefTemplate,
  css,
  ...paginationProps
}: PageNavProps) {
  const { page } = paginationProps
  const { items, hasPrevious, hasNext } = usePagination(paginationProps)

  return (
    <Container css={css}>
      {hasPrevious && (
        <StepButton
          iconBefore={<ArrowLeftIcon />}
          href={hrefTemplate(page - 1)}
        >
          <Trans id="pageNax.prev">Předchozí</Trans>
        </StepButton>
      )}
      <ol>
        {items.map((item) => {
          if (item.type === "ellipsis") {
            return (
              <li key={`ellipsis-${item.position}`}>
                <StyledMoreIcon />
              </li>
            )
          }

          return (
            <li key={item.number}>
              <Link href={hrefTemplate(item.number)} passHref>
                <IconButton
                  as="a"
                  active={item.active}
                  size="sm2"
                  aria-current={item.active ? "true" : undefined}
                >
                  {item.number}
                </IconButton>
              </Link>
            </li>
          )
        })}
      </ol>
      {hasNext && (
        <StepButton
          iconAfter={<ArrowRightIcon />}
          href={hrefTemplate(page + 1)}
        >
          <Trans id="pageNav.next">Další</Trans>
        </StepButton>
      )}
    </Container>
  )
}

type PageNavProps = UsePaginationProps & {
  hrefTemplate: (n: number) => LinkProps["href"]
  css?: CSS
}

export function PageNav({ css, ...props }: PageNavProps) {
  if (props.totalPages === 1) {
    return null
  }

  return (
    <Box css={css} textAlign="center">
      <PageNavContainer
        {...props}
        siblingCount={0}
        css={{
          alignSelf: "center",
          display: "inline-flex",
          "@sm": { display: "none" },
        }}
      />
      <PageNavContainer
        {...props}
        css={{
          display: "none",
          "@sm": { display: "inline-flex" },
          "@lg": { display: "flex" },
        }}
      />
    </Box>
  )
}
